// src/pages/onboarddeboard/employees/payroll/page.js

import React, { useEffect, useState, useRef } from "react";
import { getAllEmployees } from "../../../api/onboarddeboard";

// ─── Constants ───────────────────────────────────────────────────────────────

const AVATAR_BG = "#dce8fb";
const AVATAR_FG = "#2563eb";

const STATUS_OPTIONS = ["All status", "Processed", "Pending", "On Hold"];

const MOCK_SALARY = [
  { gross: 45000, deductions: 5400, net: 39600, status: "Processed" },
  { gross: 38000, deductions: 4560, net: 33440, status: "Pending"   },
  { gross: 52000, deductions: 6240, net: 45760, status: "Processed" },
  { gross: 41000, deductions: 4920, net: 36080, status: "Pending"   },
  { gross: 47000, deductions: 5640, net: 41360, status: "Processed" },
  { gross: 35000, deductions: 4200, net: 30800, status: "Processed" },
  { gross: 60000, deductions: 7200, net: 52800, status: "On Hold"   },
  { gross: 43000, deductions: 5160, net: 37840, status: "Processed" },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const buildName = (emp) => {
  if (emp.firstName && emp.firstName.trim()) {
    return `${emp.firstName.trim()} ${(emp.lastName || "").trim()}`.trim();
  }
  if (emp.name && emp.name.trim() && emp.name !== "—") {
    return emp.name.trim();
  }
  return "";
};

const getInitials = (name) => {
  if (!name || !name.trim()) return null;
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase())
    .join("")
    .slice(0, 2);
};

const formatINR = (num) => {
  if (num === null || num === undefined) return "—";
  return Number(num).toLocaleString("en-IN");
};

// ─── Number to Words ──────────────────────────────────────────────────────────

const numberToWords = (num) => {
  const a = [
    "", "One","Two","Three","Four","Five","Six","Seven","Eight","Nine",
    "Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen",
    "Seventeen","Eighteen","Nineteen",
  ];
  const b = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];

  const inWords = (n) => {
    if (n === 0) return "";
    if (n < 20) return a[n] + " ";
    if (n < 100) return b[Math.floor(n / 10)] + " " + a[n % 10] + " ";
    if (n < 1000) return a[Math.floor(n / 100)] + " Hundred " + inWords(n % 100);
    if (n < 100000) return inWords(Math.floor(n / 1000)) + "Thousand " + inWords(n % 1000);
    if (n < 10000000) return inWords(Math.floor(n / 100000)) + "Lakh " + inWords(n % 100000);
    return inWords(Math.floor(n / 10000000)) + "Crore " + inWords(n % 10000000);
  };

  const n = Math.round(Number(num) || 0);
  return inWords(n).trim() || "Zero";
};

// ─── Payslip Download ─────────────────────────────────────────────────────────

const downloadPayslip = (emp) => {
  const currentDate = new Date();
  const monthNames = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December",
  ];
  const month = monthNames[currentDate.getMonth()];
  const year  = currentDate.getFullYear();

  const pf         = Math.round((emp.gross || 0) * 0.12);
  const tax        = Math.round((emp.deductions || 0) - pf);
  const hra        = Math.round((emp.gross || 0) * 0.40);
  const basic      = Math.round((emp.gross || 0) * 0.50);
  const allowances = Math.round((emp.gross || 0) - basic - hra);

  const statusBg =
    emp.status === "Processed" ? "#dcfce7" :
    emp.status === "Pending"   ? "#ffedd5" : "#fee2e2";
  const statusFg =
    emp.status === "Processed" ? "#16a34a" :
    emp.status === "Pending"   ? "#ea580c" : "#dc2626";

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <title>Payslip – ${emp.name} – ${month} ${year}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box;}
    body{font-family:'Segoe UI',Arial,sans-serif;background:#fff;color:#111827;}
    .page{max-width:750px;margin:0 auto;background:#fff;}
    .header{background:linear-gradient(135deg,#1d4ed8 0%,#2563eb 60%,#3b82f6 100%);color:#fff;padding:32px 36px 24px;}
    .header-top{display:flex;justify-content:space-between;align-items:flex-start;}
    .company-name{font-size:22px;font-weight:800;letter-spacing:-0.5px;}
    .company-sub{font-size:12px;opacity:0.75;margin-top:3px;}
    .payslip-badge{background:rgba(255,255,255,0.18);border:1px solid rgba(255,255,255,0.3);border-radius:6px;padding:6px 14px;font-size:11px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;}
    .period-bar{margin-top:22px;padding-top:18px;border-top:1px solid rgba(255,255,255,0.2);display:flex;gap:36px;}
    .period-item label{font-size:10px;opacity:0.65;text-transform:uppercase;letter-spacing:0.8px;display:block;margin-bottom:3px;}
    .period-item span{font-size:14px;font-weight:600;}
    .emp-section{display:flex;border-bottom:1px solid #e5e7eb;}
    .emp-info{flex:1;padding:24px 36px;border-right:1px solid #e5e7eb;}
    .emp-info h3{font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:0.8px;font-weight:600;margin-bottom:14px;}
    .emp-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px 24px;}
    .emp-field label{font-size:11px;color:#9ca3af;display:block;margin-bottom:2px;}
    .emp-field span{font-size:13px;font-weight:600;color:#111827;}
    .status-box{width:180px;padding:24px 28px;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;background:#fafafa;}
    .status-label-text{font-size:11px;color:#9ca3af;text-transform:uppercase;letter-spacing:0.8px;margin-bottom:10px;}
    .status-pill{display:inline-block;padding:5px 18px;border-radius:20px;font-size:13px;font-weight:700;background:${statusBg};color:${statusFg};}
    .salary-section{padding:24px 36px;border-bottom:1px solid #e5e7eb;}
    .section-title{font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:0.8px;font-weight:600;margin-bottom:16px;}
    .salary-table{width:100%;border-collapse:collapse;}
    .salary-table thead tr{background:#f9fafb;}
    .salary-table th{padding:9px 12px;font-size:11px;color:#6b7280;font-weight:600;text-align:left;text-transform:uppercase;letter-spacing:0.6px;border-bottom:1px solid #e5e7eb;}
    .salary-table th:last-child,.salary-table td:last-child{text-align:right;}
    .salary-table td{padding:10px 12px;font-size:13px;color:#374151;border-bottom:1px solid #f1f5f9;}
    .salary-table .total-row td{font-weight:700;color:#111827;background:#f9fafb;border-top:2px solid #e5e7eb;border-bottom:none;}
    .deduction-amount{color:#dc2626;}
    .net-section{margin:24px 36px;background:linear-gradient(135deg,#1d4ed8,#2563eb);border-radius:10px;padding:20px 28px;display:flex;justify-content:space-between;align-items:center;color:#fff;}
    .net-label{font-size:13px;opacity:0.85;margin-bottom:4px;}
    .net-amount{font-size:28px;font-weight:800;letter-spacing:-1px;}
    .net-words{font-size:11px;opacity:0.7;margin-top:4px;font-style:italic;}
    .footer{padding:18px 36px;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;align-items:center;}
    .footer-note{font-size:11px;color:#9ca3af;}
    .generated-on{font-size:11px;color:#9ca3af;}
    @media print{body{background:#fff;}.page{max-width:100%;}}
  </style>
</head>
<body>
<div class="page">
  <div class="header">
    <div class="header-top">
      <div>
        <div class="company-name">Your Company</div>
        <div class="company-sub">Human Resources · Payroll Division</div>
      </div>
      <div class="payslip-badge">Payslip</div>
    </div>
    <div class="period-bar">
      <div class="period-item"><label>Pay Period</label><span>${month} ${year}</span></div>
      <div class="period-item"><label>Pay Date</label><span>${currentDate.toLocaleDateString("en-IN",{day:"2-digit",month:"long",year:"numeric"})}</span></div>
      <div class="period-item"><label>Employee Code</label><span>${emp.empCode}</span></div>
    </div>
  </div>
  <div class="emp-section">
    <div class="emp-info">
      <h3>Employee Details</h3>
      <div class="emp-grid">
        <div class="emp-field"><label>Full Name</label><span>${emp.name || "—"}</span></div>
        <div class="emp-field"><label>Employee Code</label><span>${emp.empCode}</span></div>
        <div class="emp-field"><label>Department</label><span>${emp.department}</span></div>
        <div class="emp-field"><label>Designation</label><span>${emp.designation || "—"}</span></div>
        <div class="emp-field"><label>Bank Account</label><span>${emp.bankAccount || "XXXX XXXX 1234"}</span></div>
        <div class="emp-field"><label>PAN</label><span>${emp.pan || "XXXXX0000X"}</span></div>
      </div>
    </div>
    <div class="status-box">
      <div class="status-label-text">Status</div>
      <div class="status-pill">${emp.status}</div>
    </div>
  </div>
  <div class="salary-section">
    <div class="section-title">Earnings &amp; Deductions</div>
    <table class="salary-table">
      <thead>
        <tr><th>Component</th><th>Type</th><th>Amount (₹)</th></tr>
      </thead>
      <tbody>
        <tr><td>Basic Salary</td><td>Earning</td><td>₹${formatINR(basic)}</td></tr>
        <tr><td>House Rent Allowance (HRA)</td><td>Earning</td><td>₹${formatINR(hra)}</td></tr>
        <tr><td>Other Allowances</td><td>Earning</td><td>₹${formatINR(allowances)}</td></tr>
        <tr><td><strong>Gross Earnings</strong></td><td></td><td><strong>₹${formatINR(emp.gross)}</strong></td></tr>
        <tr><td>Provident Fund (PF)</td><td>Deduction</td><td class="deduction-amount">- ₹${formatINR(pf)}</td></tr>
        <tr><td>Professional Tax / TDS</td><td>Deduction</td><td class="deduction-amount">- ₹${formatINR(tax > 0 ? tax : 0)}</td></tr>
        <tr class="total-row"><td>Total Deductions</td><td></td><td class="deduction-amount">- ₹${formatINR(emp.deductions)}</td></tr>
      </tbody>
    </table>
  </div>
  <div class="net-section">
    <div>
      <div class="net-label">Net Pay for ${month} ${year}</div>
      <div class="net-amount">₹${formatINR(emp.net)}</div>
      <div class="net-words">Rupees ${numberToWords(emp.net)} Only</div>
    </div>
  </div>
  <div class="footer">
    <div class="footer-note">This is a system-generated payslip and does not require a signature.</div>
    <div class="generated-on">Generated on ${currentDate.toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"})}</div>
  </div>
</div>
</body>
</html>`;

  // Open a brand-new tab/window
  const newWindow = window.open("", "_blank", "width=900,height=700");
  if (!newWindow) {
    alert("Popup blocked! Please allow popups for this site to download payslips.");
    return;
  }

  newWindow.document.open();
  newWindow.document.write(html);
  newWindow.document.close();

  // Trigger print dialog in the new window after it renders
  newWindow.onload = () => {
    newWindow.focus();
    newWindow.print();
  };

  // Fallback in case onload already fired
  setTimeout(() => {
    newWindow.focus();
    newWindow.print();
  }, 500);
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const Avatar = ({ name }) => {
  const initials = getInitials(name);
  return (
    <div style={{
      width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
      background: AVATAR_BG,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: initials ? 13 : 16,
      fontWeight: 700,
      color: AVATAR_FG,
      userSelect: "none",
    }}>
      {initials || "—"}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    Processed: { background: "#dcfce7", color: "#16a34a" },
    Pending:   { background: "#ffedd5", color: "#ea580c" },
    "On Hold": { background: "#fee2e2", color: "#dc2626" },
  };
  const s = styles[status] || { background: "#f1f5f9", color: "#64748b" };
  return (
    <span style={{
      ...s,
      display: "inline-block", padding: "3px 12px",
      borderRadius: 20, fontSize: 12, fontWeight: 600,
      whiteSpace: "nowrap",
    }}>
      {status}
    </span>
  );
};

const StatCard = ({ value, label, borderColor, prefix = "" }) => (
  <div style={{
    flex: 1, minWidth: 180,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderTop: `3px solid ${borderColor}`,
    borderRadius: 10,
    padding: "18px 22px",
    boxSizing: "border-box",
  }}>
    <div style={{ fontSize: 26, fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>
      {prefix}{value}
    </div>
    <div style={{ fontSize: 13, color: "#6b7280", marginTop: 5 }}>{label}</div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const Payroll = () => {
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  const [employees,    setEmployees]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState("");
  const [searchQuery,  setSearchQuery]  = useState("");
  const [statusFilter, setStatusFilter] = useState("All status");
  const [showDropdown, setShowDropdown] = useState(false);

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await getAllEmployees(token);
        const data = Array.isArray(res?.data) ? res.data
                   : Array.isArray(res)        ? res
                   : [];

        const enriched = data.map((emp, i) => ({
          id:          emp.userInfoId ?? emp.id ?? i,
          name:        buildName(emp),
          empCode:     emp.empCode || `EMP${String(i + 1).padStart(3, "0")}`,
          department:  emp.departmentName || emp.department || "—",
          designation: emp.designation || emp.jobTitle || "—",
          bankAccount: emp.bankAccount || emp.accountNumber || null,
          pan:         emp.pan || emp.panNumber || null,
          gross:       emp.grossSalary      ?? emp.gross      ?? MOCK_SALARY[i % MOCK_SALARY.length].gross,
          deductions:  emp.totalDeductions  ?? emp.deductions ?? MOCK_SALARY[i % MOCK_SALARY.length].deductions,
          net:         emp.netSalary        ?? emp.net        ?? MOCK_SALARY[i % MOCK_SALARY.length].net,
          status:      emp.payrollStatus    ?? emp.salaryStatus ?? MOCK_SALARY[i % MOCK_SALARY.length].status,
        }));

        setEmployees(enriched);
      } catch (err) {
        console.error("Payroll fetch error:", err);
        setError("Could not load payroll data. Showing placeholder records.");
        setEmployees(
          Array.from({ length: 4 }, (_, i) => ({
            id:          i,
            name:        "Employee Name",
            empCode:     `EMP${String(i + 1).padStart(3, "0")}`,
            department:  ["Engineering","Engineering","Testing","Engineering"][i],
            designation: "Software Engineer",
            bankAccount: null,
            pan:         null,
            ...MOCK_SALARY[i % MOCK_SALARY.length],
          }))
        );
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, [token]);

  const processedCount = employees.filter((e) => e.status === "Processed").length;
  const pendingCount   = employees.filter((e) => e.status === "Pending").length;
  const totalPayroll   = employees.reduce((sum, e) => sum + (Number(e.net) || 0), 0);
  const avgSalary      = employees.length > 0 ? Math.round(totalPayroll / employees.length) : 0;

  const filtered = employees.filter((emp) => {
    const q = searchQuery.toLowerCase();
    const matchSearch =
      emp.name.toLowerCase().includes(q)       ||
      emp.empCode.toLowerCase().includes(q)    ||
      emp.department.toLowerCase().includes(q);
    const matchStatus =
      statusFilter === "All status" || emp.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div style={{
      padding: "24px 28px 40px",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      background: "#f8fafc",
      minHeight: "calc(100vh - 60px)",
      boxSizing: "border-box",
    }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: 24 }}>
       <h2 className="page-main-heading">Payroll</h2>
<p className="page-sub-heading">Manage salary processing and payslips</p>
      </div>

      {/* ── Stat Cards ── */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
        <StatCard value={loading ? "—" : formatINR(totalPayroll)} label="Total Payroll" borderColor="#22c55e" prefix="₹" />
        <StatCard value={loading ? "—" : processedCount}          label="Processed"     borderColor="#3b82f6" />
        <StatCard value={loading ? "—" : pendingCount}            label="Pending"       borderColor="#f59e0b" />
        <StatCard value={loading ? "—" : formatINR(avgSalary)}    label="Avg. Salary"   borderColor="#8b5cf6" prefix="₹" />
      </div>

      {/* ── Search + Filter ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <div style={{ position: "relative", flex: 1 }}>
          <svg
            width="14" height="14" viewBox="0 0 24 24" fill="none"
            style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
          >
            <circle cx="11" cy="11" r="7" stroke="#9ca3af" strokeWidth="2" />
            <path d="M16.5 16.5L21 21" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search employees..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%", height: 36, padding: "0 12px 0 34px",
              border: "1px solid #e2e8f0", borderRadius: 8,
              fontSize: 13, color: "#374151", background: "#fff",
              outline: "none", boxSizing: "border-box", fontFamily: "inherit",
            }}
          />
        </div>

        <div style={{ position: "relative", flexShrink: 0 }} ref={dropdownRef}>
          <button
            onClick={() => setShowDropdown((v) => !v)}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              height: 36, padding: "0 14px",
              border: "1px solid #e2e8f0", borderRadius: 8,
              background: "#fff", fontSize: 13, color: "#374151",
              cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
            }}
          >
            {statusFilter}
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path d="M9 18l6-6-6-6" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {showDropdown && (
            <div style={{
              position: "absolute", top: "calc(100% + 4px)", right: 0,
              background: "#fff", border: "1px solid #e2e8f0",
              borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
              zIndex: 50, minWidth: 170, overflow: "hidden",
            }}>
              {STATUS_OPTIONS.map((opt) => (
                <div
                  key={opt}
                  onClick={() => { setStatusFilter(opt); setShowDropdown(false); }}
                  style={{
                    padding: "9px 14px", fontSize: 13, cursor: "pointer",
                    color: statusFilter === opt ? "#2563eb" : "#374151",
                    fontWeight: statusFilter === opt ? 600 : 400,
                    background: statusFilter === opt ? "#eff6ff" : "transparent",
                  }}
                  onMouseEnter={(e) => {
                    if (statusFilter !== opt) e.currentTarget.style.background = "#f9fafb";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = statusFilter === opt ? "#eff6ff" : "transparent";
                  }}
                >
                  {opt}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
        {loading ? (
          <div style={{
            padding: 48, textAlign: "center", color: "#9ca3af",
            fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          }}>
            <LoadingSpinner />
            Loading payroll data…
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                {["Employee","Department","Gross Salary","Deductions","Net Salary","Status","Actions"].map((col) => (
                  <th key={col} style={{
                    padding: "12px 16px", textAlign: "left",
                    fontSize: 13, fontWeight: 600, color: "#374151", whiteSpace: "nowrap",
                  }}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: 40, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                    No employees match your search.
                  </td>
                </tr>
              ) : (
                filtered.map((emp) => (
                  <tr
                    key={emp.id}
                    style={{ borderBottom: "1px solid #f1f5f9" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#fafafa"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                  >
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Avatar name={emp.name} />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                            {emp.name || "—"}
                          </div>
                          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1 }}>
                            {emp.empCode}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "#374151" }}>{emp.department}</td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "#374151" }}>₹{formatINR(emp.gross)}</td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "#374151" }}>₹{formatINR(emp.deductions)}</td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: "#374151" }}>₹{formatINR(emp.net)}</td>
                    <td style={{ padding: "12px 16px" }}><StatusBadge status={emp.status} /></td>
                    <td style={{ padding: "12px 16px" }}>
                      <PayslipButton onClick={() => downloadPayslip(emp)} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {error && (
        <p style={{ marginTop: 10, fontSize: 12, color: "#f59e0b" }}>⚠ {error}</p>
      )}
    </div>
  );
};

// ─── Small Components ─────────────────────────────────────────────────────────

const PayslipButton = ({ onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center", gap: 5,
        background: hovered ? "#eff6ff" : "none",
        border: `1px solid ${hovered ? "#2563eb" : "#e5e7eb"}`,
        borderRadius: 6, padding: "5px 10px",
        fontSize: 12,
        color: hovered ? "#2563eb" : "#374151",
        cursor: "pointer",
        transition: "all 0.15s",
        fontFamily: "inherit",
      }}
    >
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
        <path
          d="M8 1v9M8 10l-3-3M8 10l3-3M2 13h12"
          stroke={hovered ? "#2563eb" : "#6b7280"}
          strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
        />
      </svg>
      Payslip
    </button>
  );
};

const LoadingSpinner = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" style={{ animation: "spin 0.8s linear infinite" }}>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    <circle cx="9" cy="9" r="7" stroke="#e5e7eb" strokeWidth="2.5" fill="none" />
    <path d="M9 2a7 7 0 017 7" stroke="#2563eb" strokeWidth="2.5" fill="none" strokeLinecap="round" />
  </svg>
);

export default Payroll;